import React, { Fragment, useEffect, useImperativeHandle, useState } from "react";
import event from "@/util/event";
import lib from "@/util/lib";
import { Form, Row, Modal } from "antd";
import axios from "axios";
import "./config-form-center.less";
import moment from "moment";
import { forwardRef } from "react";
import { formItems } from "./formitems";
import _ from "lodash";
import { generateUUID, getConfig } from "./utils";
import { uid } from "./core/uid";

/**
 *  示例：
 // <ConfigFormCenter
 //     insertContainer={insertContainer}
 //     ref={ref}
 //     code={code}
 //     confId={333}
 //     beforeSubmit={beforeSubmit}
 //     onConfigLoadSuccess={onConfigLoadSuccess}
 //     onInitSelectSuccess={onInitSelectSuccess}
 //     onSetDetailSuccess={onSetDetailSuccess}
 //     onSetDetailFail={onSetDetailFail}
 //     submitUrl={'/dt-wms-portal/prePackagePlan/lockPrePackageSkuLot'}
 // />
 *
 * @type {React.ForwardRefExoticComponent<React.PropsWithoutRef<{}> & React.RefAttributes<unknown>>}
 */
const ConfigFormCenter = forwardRef((prop, ref) => {
    const id = React.useRef(uid());
    let [refresh, setRefresh] = useState(0);
    let [changConfigCount, setChangConfigCount] = useState(0);
    const [config, setConfig] = useState(prop.confData);
    const [pageData, setPageData] = useState({});
    let [selectList, setSelectList] = useState({});
    let disableEdit = prop.disableEdit;
    let labelWrap = prop.formProps?.labelWrap || false;
    let layout = prop.formProps?.layout || "horizontal";
    let formItemLayout =
        prop.formProps?.formItemLayout ||
        (layout !== "vertical" && { labelCol: { span: 8 }, wrapperCol: { span: 16 } });

    function getMariaConfig() {
        axios
            .get(prop.configUrl)
            .then(json => json.data.data)
            .then(list => {
                setConfig(list);
                prop.onConfigLoadSuccess && prop.onConfigLoadSuccess(list);
            });
    }

    useEffect(() => {
        if (config) {
            prop.onConfigLoadSuccess && prop.onConfigLoadSuccess(config);
        } else if (prop.configUrl) {
            // 获取maria数据
            getMariaConfig();
        }
        event.on("singlesSelectChange", singlesSelectChange);
        event.on("inputChange", inputChange);
        event.on("radioSelectChange", radioSelectChange);
        event.on("checkboxSelectChange", checkboxSelectChange);
        event.on("numberInput", numberInput);
        event.on("inputAndSelect", inputAndSelect);
        return () => {
            event.off("singlesSelectChange", singlesSelectChange);
            event.off("inputChange", inputChange);
            event.off("radioSelectChange", radioSelectChange);
            event.off("checkboxSelectChange", checkboxSelectChange);
            event.off("numberInput", numberInput);
            event.off("inputAndSelect", inputAndSelect);
        };
    }, []);

    const singlesSelectChange = desc => {
        if (id.current === desc.formId)
            //暂时只支持单form通知Change事件
            prop.onSinglesSelectChange && prop.onSinglesSelectChange(desc);
    };
    const inputChange = desc => {
        if (id.current === desc.formId) prop.onSinglesSelectChange && prop.onSinglesSelectChange(desc);
    };
    const radioSelectChange = desc => {
        if (id.current === desc.formId) prop.onSinglesSelectChange && prop.onSinglesSelectChange(desc);
    };
    const checkboxSelectChange = desc => {
        if (id.current === desc.formId) prop.onSinglesSelectChange && prop.onSinglesSelectChange(desc);
    };
    const numberInput = desc => {
        if (id.current === desc.formId) prop.onSinglesSelectChange && prop.onSinglesSelectChange(desc);
    };
    const inputAndSelect = desc => {
        if (id.current === desc.formId) prop.onSinglesSelectChange && prop.onSinglesSelectChange(desc);
    };

    useEffect(() => {
        if (changConfigCount > 0) {
            setDetail(pageData);
        }
    }, [changConfigCount]);

    // 获取select数据
    function initSelect(item, param) {
        if (item.from) {
            let data = {};
            if (item.fromParam) {
                let fromParam;
                if (typeof item.fromParam != "object") {
                    fromParam = JSON.parse(item.fromParam);
                } else {
                    fromParam = item.fromParam;
                }
                fromParam.forEach(value => {
                    data[value.name || value.alias] = form.getFieldValue(value.name);
                });
            }
            Object.assign(data, param);
            lib.request({
                data: data,
                url: item.from,
                needMask: false,
                success: data => {
                    let selectData = {};
                    selectData[item.name] = data;
                    setSelectList(Object.assign(selectList, selectData));
                    prop.onInitSelectSuccess && prop.onInitSelectSuccess(item);
                    setRefresh(++refresh);
                },
            });
        }
    }

    const onFinish = values => {
        if (prop.confirmContent && prop.confirmContent.length > 0) {
            confirm(values, prop.confirmContent);
        } else {
            let data = prop.beforeSubmit && prop.beforeSubmit(values);
            if (data) {
                submit(data);
            } else if (values) {
                submit(values);
            }
        }
    };
    const submit = data => {
        if (prop.submitUrl) {
            lib.request({
                data: data,
                url: prop.submitUrl,
                needMask: true,
                success: data => {
                    prop.onSubmitSuccess && prop.onSubmitSuccess(data);
                },
                fail: (code, msg) => {
                    lib.waitEnd();
                },
            });
        }
    };

    /**
     * 确认弹框
     * @param values
     * @param confirmContent 提示语
     */
    function confirm(values, confirmContent) {
        Modal.confirm({
            title: "提交提示",
            content: confirmContent,
            okText: "确认",
            cancelText: "取消",
            onOk: () => {
                let data = prop.beforeSubmit && prop.beforeSubmit(values);
                if (data) {
                    submit(data);
                } else if (values) {
                    submit(values);
                }
            },
        });
    }

    /**
     * 获取页面详情
     * @param url URL
     * @param data 参数
     */
    function getDetail({ url, data }) {
        lib.request({
            url: url,
            needMask: true,
            data: data,
            success: data => {
                let detailData = prop.beforeSetDetail ? prop.beforeSetDetail(data) : data;
                setPageData(detailData);
                setDetail(detailData);
            },
            fail: (code, msg) => {
                lib.waitEnd();
                prop.onSetDetailFail && prop.onSetDetailFail();
            },
        });
    }

    useImperativeHandle(ref, () => ({
        getDetail: getDetail,
        initSelect: initSelect,
        setMergeDetail: setMergeDetail,
        config: config,
        changeConfig: changeConfig,
        submitForm: submitForm,
        getForm: form,
        reset: reset,
        getFormFiled: getFormFiled,
        getFormFieldsValue: getFormFieldsValue,
        getSelectList: getSelectList,
        updateSelectList: updateSelectList,
        resetChangConfigCount: resetChangConfigCount,
        formId: id,
    }));

    function resetChangConfigCount() {
        setChangConfigCount(0);
    }

    function getSelectList(item) {
        return selectList[item.name];
    }

    function updateSelectList(item, list) {
        let selectData = {};
        selectData[item.name] = list;
        setSelectList(Object.assign(selectList, selectData));
        setRefresh(++refresh);
    }

    /**
     * 获取单个表单字段值
     * @param data
     */
    function getFormFiled(name) {
        return form.getFieldValue(name);
    }

    /**
     * 获取全部表单字段值
     * @param data
     */
    function getFormFieldsValue() {
        return form.getFieldsValue();
    }

    function reset() {
        if (config) {
            Object.keys(config).map(function (key) {
                let configElement = config[key];
                if (configElement.isGroup === true) {
                    configElement.children.map((item, index) => {
                        delete item.value;
                    });
                } else {
                    delete configElement.value;
                }
            });
        }
    }

    function submitForm() {
        form.submit();
    }

    /**
     * 修改页面配置属性
     * @param config 页面配置
     */
    function changeConfig(config) {
        setConfig(config);
        setChangConfigCount(++changConfigCount);
    }

    /**
     * 更新页面数据源，合并字段值
     * @param data
     */
    function setMergeDetail(data) {
        _.merge(pageData, data);
        setPageData(pageData);
        setChangConfigCount(++changConfigCount);
    }

    /**
     * 设置页面详情
     * @param data
     */
    function setDetail(data) {
        let fieldsValue = {};
        let tableDataSource = []; // tableDataSource [{tableName: item.name, data: list}]

        /**
         *  数据拆分：如果该表格有数据拆分则根据组件外部拆分逻辑拆分数据
         *  返回table数据源
         * @param item 表格配置
         * @param datum 数据源
         */
        function checkSplitData(item, datum) {
            if (item.customStr && typeof item.customStr != "object") {
                Object.assign(item, JSON.parse(item.customStr));
            }
            if (item.split) {
                return prop.splitData(datum);
            } else {
                return datum;
            }
        }

        /**
         * 处理表单item字段值
         * @param item 配置项属性
         * @param datum 字段数据值
         */
        function handleItemFieldsValue(item, datum) {
            if (item.type === "dateInput" && datum) {
                if (getConfig(item).isRange) {
                    if (Array.isArray(datum)) {
                        datum.map(item => moment(item));
                    }
                    _.set(fieldsValue, item.name, datum);
                } else {
                    _.set(fieldsValue, item.name, moment(datum));
                }
            } else if (item.type === "tableInput") {
                let list = checkSplitData(item, datum);
                //table row 必须存在key
                if (list) {
                    list.map((value, index) => {
                        value.key = index;
                    });
                    tableDataSource.push({ tableName: `${item.name}-${item.key}`, data: list });
                    _.set(fieldsValue, item.name, list);
                }
            } else {
                _.set(fieldsValue, item.name, datum);
            }
        }

        /**
         * 根据配置中单个item项获取数据
         * @param item
         * @returns {*}
         */
        function getItemData(item) {
            let datum = _.get(data, item.name);
            if (typeof datum === "undefined") {
                datum = _.get(data, item.alias);
            }
            return datum;
        }

        /**
         * 表格设置数据源，填充数据，
         * 依据tableDataSource(Array)遍历更新table数据dataSource
         */
        function setTableDataSource() {
            tableDataSource.forEach(value => {
                event.emit(`setTableDataSource-${value.tableName}`, value);
            });
        }

        Object.keys(config).forEach(function (key) {
            let configElement = config[key];

            if (configElement.isGroup === true) {
                configElement.children &&
                    configElement.children.map((item, index) => {
                        let datum = getItemData(item);
                        if (typeof datum !== "undefined") {
                            handleItemFieldsValue(item, datum);
                        }
                    });
            } else if (configElement.isGroup === false) {
                let datum = getItemData(configElement);
                handleItemFieldsValue(configElement, datum);
            }
        });
        console.log("fieldsValue", fieldsValue);
        //设置表单字段值，页面FormItem会赋值
        form.setFieldsValue(fieldsValue);
        //刷新下table，通知数据源dataSource变化
        setTableDataSource();
        //页面详情设置成功
        prop.onSetDetailSuccess && prop.onSetDetailSuccess(fieldsValue, changConfigCount);
    }

    const [form] = Form.useForm();

    /**
     * 构造多个组件组元素
     * @param configElement
     * @param key
     * @returns {JSX.Element}
     */
    function makeGroupElement(configElement, key) {
        return (
            <Row className={`group-component ${configElement.className ? configElement.className : ""}`} key={key}>
                <div className={"title"}>
                    <div className="subtitle">
                        {configElement.label}
                        {configElement.titleAction && configElement.titleAction()}
                    </div>
                </div>

                {configElement.children &&
                    configElement.children.map((item, index) => {
                        if (!item.key) {
                            item.key = generateUUID(item.name);
                        }
                        const Control = formItems[item.type];

                        return (
                            !item.hidden &&
                            Control && (
                                <Control
                                    form={form}
                                    item={item}
                                    infoEdit={disableEdit}
                                    key={index}
                                    selectList={selectList[item.name]}
                                    allSelectList={selectList}
                                    parentFormItemLayout={formItemLayout}
                                    layout={layout}
                                    formId={id.current}>
                                    {
                                        //如果组件元素是容器的，需要放入子标签，在外部实现。
                                        (function () {
                                            if (item.type === "container") {
                                                return prop.insertContainer(item);
                                            } else {
                                                return null;
                                            }
                                        })()
                                    }
                                </Control>
                            )
                        );
                    })}
            </Row>
        );
    }

    /**
     * 构造单个控件元素
     * @param configElement 配置
     * @param key key
     * @returns {JSX.Element}
     */
    function makeSingeElement(configElement, key) {
        let Control = configElement && formItems[configElement.type];
        if (!configElement.key) {
            configElement.key = generateUUID(configElement.name);
        }
        if (Control) {
            return (
                <Row className={`group-component ${configElement.className}`} key={key}>
                    <div className={"title"}>
                        <div className="subtitle">
                            {configElement.label} {configElement.titleAction && configElement.titleAction()}
                        </div>
                    </div>
                    {Control && (
                        <Control
                            form={form}
                            item={configElement}
                            infoEdit={disableEdit}
                            selectList={selectList[configElement.name]}
                            allSelectList={selectList}
                            parentFormItemLayout={formItemLayout}
                            layout={layout}
                            formId={id.current}>
                            {" "}
                            {
                                //如果组件元素是容器的，需要放入子标签，在外部实现。
                                (function () {
                                    if (configElement.type === "container") {
                                        return prop.insertContainer(configElement);
                                    } else {
                                        return null;
                                    }
                                })()
                            }
                        </Control>
                    )}
                </Row>
            );
        } else {
            return <Fragment key={key} />;
        }
    }

    const onValuesChange = (changedValues, values) => {
        // 目标值存在，undefined的Value将会被合并处理
        setPageData(
            _.mergeWith(pageData, changedValues, function (objValue, srcValue) {
                if (objValue !== undefined && srcValue === undefined) {
                    return null;
                }
                return undefined;
            }),
        );
    };
    return (
        <div className="content-form-main">
            <Form
                onFinish={onFinish}
                form={form}
                {...formItemLayout}
                layout={layout}
                labelWrap={labelWrap}
                onValuesChange={onValuesChange}
                autoComplete="off">
                {
                    //根据配置map出页面控件
                    config &&
                        Object.keys(config).map(function (key) {
                            let configElement = config[key];
                            if (!configElement.hideen) {
                                if (configElement.isGroup === true) {
                                    return makeGroupElement(configElement, key);
                                } else {
                                    return makeSingeElement(configElement, key);
                                }
                            } else {
                                return <Fragment key={key} />;
                            }
                        })
                }
            </Form>
        </div>
    );
});

export default ConfigFormCenter;
